import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router';
import { QRCodeSVG } from 'qrcode.react';
import { api, completeLogin, describeAuthRefusal, describeError } from '@/lib/api';
import { postLoginPath } from '@/lib/mcp-consent';
import { AuthCard } from './AuthCard';

interface EnrollBeginResponse {
  secret: string;
  otpAuthUri: string;
}

interface CompleteResponse {
  accessToken: string;
  recoveryCodes?: string[];
}

export function MfaEnrollPage() {
  const navigate = useNavigate();

  const [secret, setSecret] = useState<string | null>(null);
  const [uri, setUri] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);

  // Kick off enroll on mount — generates and returns the secret.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.post<EnrollBeginResponse>('/platform/auth/mfa/enroll/begin');
        if (cancelled) return;
        setSecret(data.secret);
        setUri(data.otpAuthUri);
      } catch (err) {
        if (!cancelled) setError(describeError(err));
      }
    })();
    return () => { cancelled = true; };
  }, []);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const { data } = await api.post<CompleteResponse>(
        '/platform/auth/mfa/enroll/complete',
        { code },
      );
      // Só o access, em memória: o refresh chega no cookie platform_refresh (HttpOnly).
      completeLogin(data);
      setRecoveryCodes(data.recoveryCodes ?? []);
    } catch (err) {
      // O complete confere o fator: 423 da conta bloqueada e 429 têm mensagem própria.
      setError(describeAuthRefusal(err));
    } finally {
      setSubmitting(false);
    }
  }

  if (recoveryCodes) {
    return (
      <AuthCard
        title="Salve os recovery codes"
        subtitle="Estes códigos são exibidos APENAS uma vez. Guarde em local seguro."
      >
        <div className="info-banner">
          Cada código funciona uma única vez como alternativa ao TOTP. Sem
          eles e sem o seu celular, a única forma de recuperar a conta é via
          intervenção administrativa.
        </div>
        <ol className="mono" style={{ paddingLeft: 24, marginBottom: 16 }}>
          {recoveryCodes.map((c) => <li key={c} style={{ marginBottom: 4 }}>{c}</li>)}
        </ol>
        {/* Com autorização de agente pendente, volta ao caminho fixo do consentimento. */}
        <button className="btn" style={{ width: '100%' }} onClick={() => navigate(postLoginPath())}>
          Já guardei — continuar
        </button>
      </AuthCard>
    );
  }

  return (
    <AuthCard
      title="Configurar MFA"
      subtitle="Escaneie no Google Authenticator / 1Password / Authy e digite o primeiro código."
    >
      <form onSubmit={onSubmit}>
        {error && <div className="error-banner">{error}</div>}

        {secret && uri ? (
          <>
            {/* QR generated client-side from the otpauth URI — the secret never
                leaves the browser (no external QR image service). */}
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
              <div style={{ background: 'var(--color-surface)', padding: 12, borderRadius: 'var(--radius-md)' }}>
                <QRCodeSVG value={uri} size={184} level="M" />
              </div>
            </div>

            <details style={{ marginBottom: 16 }}>
              <summary className="muted" style={{ fontSize: 13, cursor: 'pointer' }}>
                Não consegue escanear? Insira manualmente
              </summary>

              <p className="muted" style={{ fontSize: 13, marginTop: 12 }}>
                Segredo (base32):
              </p>
              <div className="mono" style={{
                padding: 12,
                background: 'var(--color-surface-alt)',
                borderRadius: 'var(--radius-md)',
                marginBottom: 12,
                fontSize: 13,
              }}>
                {secret}
              </div>

              <p className="muted" style={{ fontSize: 13 }}>otpauth URI:</p>
              <div className="mono" style={{
                wordBreak: 'break-all',
                padding: 12,
                background: 'var(--color-surface-alt)',
                borderRadius: 'var(--radius-md)',
                fontSize: 12,
              }}>
                {uri}
              </div>
            </details>
          </>
        ) : (
          <p className="muted">Gerando segredo…</p>
        )}

        <div className="field">
          <label htmlFor="code">Código TOTP (6 dígitos)</label>
          <input
            id="code"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            maxLength={6}
            required
          />
        </div>

        <button type="submit" className="btn" style={{ width: '100%' }} disabled={submitting || !secret}>
          {submitting ? 'Validando…' : 'Confirmar e entrar'}
        </button>
      </form>
    </AuthCard>
  );
}
